import React, { useEffect, useMemo, useState } from "react";

import PitchesService, { Pitch } from "@services/Pitches.service";
import { notification, Table } from "antd";
import { getPitchesColumns } from "../utils/const";
import Loader from "@appComponents/Loader/Loader";
import { DESKTOP_WIDTH } from "@bundles/consts";
import { useMediaQuery } from "react-responsive";
import { PitchMobileExpandedRow } from "./PitchMobileExpandedRow";

type PitchesListProps = {
  searchQuery: string;
};

export const PitchesList = ({ searchQuery }: PitchesListProps) => {
  const isMobile = useMediaQuery({ maxWidth: DESKTOP_WIDTH });

  const [pitchesLoading, setPitchesLoading] = useState(true);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [filteredPitches, setFilteredPitches] = useState<Pitch[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpandToggle = (record: Pitch) => {
    setExpandedRowKeys((prev) =>
      prev.includes(record.id)
        ? prev.filter((id) => id !== record.id)
        : [...prev, record.id]
    );
  };

  const pitchesColumns = useMemo(
    () =>
      getPitchesColumns({
        isMobile,
        handleExpandToggle,
        expandedRowKeys,
      }),
    [isMobile, handleExpandToggle, expandedRowKeys]
  );

  useEffect(() => {
    PitchesService.getPitches()
      .then((resp) => {
        setPitchesLoading(false);
        setPitches(resp.data);
      })
      .catch(() => {
        setPitchesLoading(false);
        notification.error({ message: "Something went wrong." });
      });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!searchQuery) {
        setFilteredPitches(pitches);
      } else {
        const lowerSearch = searchQuery.toLowerCase();
        setFilteredPitches(
          pitches.filter((pitch) =>
            pitch.title?.toLowerCase().includes(lowerSearch)
          )
        );
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery, pitches]);

  if (pitchesLoading) {
    return <Loader />;
  }

  return (
    <div className="mt-[20px] md:mt-[50px]">
      <Table
        className="tailwind-table-white"
        columns={pitchesColumns}
        dataSource={filteredPitches}
        pagination={false}
        rowKey="id"
        expandable={
          isMobile
            ? {
                expandedRowKeys,
                defaultExpandAllRows: false,
                showExpandColumn: false,
                expandedRowClassName: () => "bg-grey-light",
                expandedRowRender: (record) => (
                  <PitchMobileExpandedRow pitch={record} />
                ),
              }
            : undefined
        }
      />
    </div>
  );
};
